import { useState } from 'react';
import { personInitials } from '@voll-intelligence/intelligence-ui-kit';

import type {
  ApprovalStackBlock,
  ApprovalStackTraveler,
  ApprovalStackTripGroup,
  ApprovalStackVoucher,
} from '@voll-intelligence/genui-contract';
import type { GenUiActionAnalytics } from '../../../shared/ui/action.js';
import { CardFooter } from '../../../shared/ui/card.js';
import { CaretDownIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { fmtDateRange, fmtMoney } from '../../../shared/trip/trip_format.js';
import { MODAL_UI } from '../../../shared/trip/trip_maps.js';
import { ModalIcon } from '../../../shared/trip/trip_primitives.js';
import { VoucherAccordion } from '../../../shared/trip/voucher_accordion.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';
import '../../shared/approval.css';
import './approval_stack.css';

/**
 * Card empilhado de aprovações (`approval_stack`) — a experiência de primeira
 * classe que substitui o texto narrado (UX-02). Hierarquia viajante → viagem →
 * voucher reusando os primitivos do card `trips` (`VoucherAccordion`,
 * `ModalIcon`, `fmtMoney`/`fmtDateRange`); sem nova linguagem visual.
 *
 * Determinismo (UX-03/04): cada CTA ecoa um challengeId ESTRUTURADO armado pelo
 * agente — o LLM nunca compõe contagem nem decisão. Per-voucher → `decideChallengeId`
 * via `approval-decision`; cada grupo (viajante/viagem/avulsos) e o topo → o
 * `bulkChallengeId`/`allChallengeId` com o `scope` determinístico via
 * `approval-bulk-decision`. Bulk é approve-only (UX-06); reprovar é per-voucher
 * com justificativa opcional. `readOnly` congela: zero CTAs.
 */
export type ApprovalStackDispatch = (
  action:
    | {
        type: 'approval-decision';
        challengeId: string;
        decision: 'approve' | 'deny';
        justification?: string;
        text: string;
        analytics?: GenUiActionAnalytics;
      }
    | {
        type: 'approval-bulk-decision';
        challengeId: string;
        scope: 'all' | { tripId: string } | { travelerId: string };
        text: string;
        analytics?: GenUiActionAnalytics;
      },
) => void;

/** Linha de um voucher: o acordeão reusado + os CTAs Aprovar/Reprovar. */
function VoucherRow({
  voucher,
  readOnly,
  onDispatch,
}: {
  readonly voucher: ApprovalStackVoucher;
  readonly readOnly: boolean;
  readonly onDispatch?: ApprovalStackDispatch;
}) {
  const { t } = useGenUiI18n();
  const [sent, setSent] = useState(false);
  const [showJustify, setShowJustify] = useState(false);
  const [justification, setJustification] = useState('');

  const decide = (decision: 'approve' | 'deny') => {
    setSent(true);
    const trimmed = justification.trim();
    onDispatch?.({
      type: 'approval-decision',
      challengeId: voucher.decideChallengeId,
      decision,
      ...(decision === 'deny' && trimmed.length > 0 ? { justification: trimmed } : {}),
      text:
        decision === 'approve' ? t('approval.textApproveVoucher') : t('approval.textDenyVoucher'),
      analytics: { actionId: decision },
    });
  };

  return (
    <div className="astack__voucher">
      <VoucherAccordion voucher={voucher} />
      {!readOnly && (
        <div className="astack__voucher-actions">
          <div className="fui-actions">
            <button
              type="button"
              className="fui-btn fui-btn-primary"
              disabled={sent || !onDispatch}
              onClick={() => {
                decide('approve');
              }}
            >
              <CheckIcon size={14} aria-hidden />
              {sent ? t('approval.sent') : t('approval.approve')}
            </button>
            <button
              type="button"
              className="fui-btn fui-btn-deny"
              disabled={sent || !onDispatch}
              onClick={() => {
                if (showJustify) decide('deny');
                else setShowJustify(true);
              }}
            >
              <XIcon size={14} aria-hidden />
              {t('approval.deny')}
            </button>
          </div>
          {showJustify && !sent && (
            <label className="appr-justify">
              <span className="appr-justify-label">{t('approval.justificationOptional')}</span>
              <textarea
                value={justification}
                onChange={(e) => {
                  setJustification(e.target.value);
                }}
                maxLength={500}
                placeholder={t('approval.reasonDeny')}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

/** Botão de aprovação em lote (approve-only) — ecoa o challengeId + scope. */
function BulkApproveButton({
  label,
  count,
  challengeId,
  scope,
  text,
  onDispatch,
}: {
  readonly label: string;
  readonly count: number;
  readonly challengeId: string;
  readonly scope: 'all' | { tripId: string } | { travelerId: string };
  readonly text: string;
  readonly onDispatch?: ApprovalStackDispatch;
}) {
  const { t } = useGenUiI18n();
  const [sent, setSent] = useState(false);
  return (
    <button
      type="button"
      className="fui-btn fui-btn-primary astack__bulk"
      disabled={sent || !onDispatch}
      onClick={() => {
        setSent(true);
        // Bulk é approve-only (UX-06) — o actionId reflete a única decisão possível.
        onDispatch?.({
          type: 'approval-bulk-decision',
          challengeId,
          scope,
          text,
          analytics: { actionId: 'approve' },
        });
      }}
    >
      <CheckIcon size={14} aria-hidden />
      {sent ? t('approval.sent') : `${label} (${String(count)})`}
    </button>
  );
}

/** Bloco de uma viagem — recolhido por padrão (UX-02: cabe mais na tela). */
function TripBlock({
  trip,
  readOnly,
  onDispatch,
}: {
  readonly trip: ApprovalStackTripGroup;
  readonly readOnly: boolean;
  readonly onDispatch?: ApprovalStackDispatch;
}) {
  const { t, locale } = useGenUiI18n();
  const [open, setOpen] = useState(false);
  const total = fmtMoney(trip.totalAmount, trip.currency);
  const dates = fmtDateRange(trip.dates.startAt, trip.dates.endAt, undefined, undefined, locale);
  const tripLabel = trip.tripName ?? t('approval.tripFallback');
  return (
    <div className={`astack__trip${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="astack__trip-head"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span className="astack__trip-modals">
          {trip.modals.map((m, i) => {
            const meta = MODAL_UI[m];
            return meta ? (
              <span className="astack__modal-ico" key={`${m}-${String(i)}`}>
                <ModalIcon icon={meta.icon} size={14} />
              </span>
            ) : null;
          })}
        </span>
        <span className="astack__trip-main">
          <span className="astack__trip-name">{tripLabel}</span>
          {dates && <span className="astack__trip-dates">{dates}</span>}
        </span>
        <span className="astack__count">{trip.count}</span>
        {total && <span className="astack__total">{total}</span>}
        <span className="astack__chev">
          <CaretDownIcon size={15} aria-hidden />
        </span>
      </button>
      {open && (
        <div className="astack__trip-body">
          {trip.vouchers.map((v) => (
            <VoucherRow key={v.id} voucher={v} readOnly={readOnly} onDispatch={onDispatch} />
          ))}
          {!readOnly && trip.count > 0 && (
            <BulkApproveButton
              label={t('approval.approveTrip')}
              count={trip.count}
              challengeId={trip.bulkChallengeId}
              scope={{ tripId: trip.tripId }}
              text={t('approval.textApproveTrip', { name: tripLabel })}
              onDispatch={onDispatch}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Seção de um viajante — eixo primário de agrupamento (UX-01). */
function TravelerSection({
  traveler,
  readOnly,
  onDispatch,
}: {
  readonly traveler: ApprovalStackTraveler;
  readonly readOnly: boolean;
  readonly onDispatch?: ApprovalStackDispatch;
}) {
  const { t } = useGenUiI18n();
  const [open, setOpen] = useState(true);
  const total = fmtMoney(traveler.totalAmount, traveler.currency);
  return (
    <div className={`astack__traveler${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="astack__traveler-head"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span className="astack__avatar">{personInitials(traveler.name)}</span>
        <span className="astack__traveler-main">
          <span className="astack__traveler-name">{traveler.name}</span>
        </span>
        <span className="astack__count">{traveler.count}</span>
        {total && <span className="astack__total">{total}</span>}
        <span className="astack__chev">
          <CaretDownIcon size={15} aria-hidden />
        </span>
      </button>
      {open && (
        <div className="astack__traveler-body">
          {traveler.trips.map((trip) => (
            <TripBlock key={trip.tripId} trip={trip} readOnly={readOnly} onDispatch={onDispatch} />
          ))}
          {traveler.looseVouchers.length > 0 && (
            <div className="astack__loose">
              <span className="astack__loose-label">{t('approval.loose')}</span>
              {traveler.looseVouchers.map((v) => (
                <VoucherRow key={v.id} voucher={v} readOnly={readOnly} onDispatch={onDispatch} />
              ))}
            </div>
          )}
          {/* "Aprovar do viajante" cobre TODO o conjunto pendente do viajante (viagens +
              avulsos): o escopo `traveler:<id>` no backend agora resolve os dois. Não há
              um botão "Aprovar avulsos" separado — seria o mesmo challenge/escopo, sem
              como o handler distinguir. Avulsos isolados se aprovam por-voucher. */}
          {!readOnly && traveler.count > 0 && (
            <BulkApproveButton
              label={t('approval.approveTraveler')}
              count={traveler.count}
              challengeId={traveler.bulkChallengeId}
              scope={{ travelerId: traveler.travelerId }}
              text={t('approval.textApproveTraveler', { name: traveler.name })}
              onDispatch={onDispatch}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * O card empilhado completo. `note` (texto do agente) vai pro rodapé; `readOnly`
 * congela todos os CTAs (conversa encerrada OU interação já resolvida server-side).
 */
export function ApprovalStackCard({
  ui,
  note,
  readOnly = false,
  dispatch,
}: {
  readonly ui: ApprovalStackBlock;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly dispatch?: ApprovalStackDispatch;
}) {
  const { t } = useGenUiI18n();
  const totalCount = ui.travelers.reduce((acc, t) => acc + t.count, 0);
  return (
    <section className="fui astack" aria-label={t('approval.pendingAria')}>
      <div className="fui-card">
        <div className="fui-body">
          {!readOnly && totalCount > 0 && (
            <div className="astack__top">
              <BulkApproveButton
                label={t('approval.approveAll')}
                count={totalCount}
                challengeId={ui.allChallengeId}
                scope="all"
                text={t('approval.textApproveAll')}
                onDispatch={dispatch}
              />
            </div>
          )}
          <div className="astack__list">
            {ui.travelers.map((traveler) => (
              <TravelerSection
                key={traveler.travelerId}
                traveler={traveler}
                readOnly={readOnly}
                onDispatch={dispatch}
              />
            ))}
          </div>
        </div>
        <CardFooter note={note} />
      </div>
    </section>
  );
}
